if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
      }

      connectedCallback() {
        this.init();
      }

      init() {
        if (this.dataset.atcReady === 'true') return;

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.form || !this.submitButton) {
          if (this._initTries > 10) return;
          this._initTries = (this._initTries || 0) + 1;
          requestAnimationFrame(() => this.init());
          return;
        }

        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler);
        this.submitButtonText = this.submitButton.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.getCart()?.tagName === 'CART-DRAWER') {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.dataset.atcReady = 'true';
        this.flushQueuedSubmit();
      }

      getCart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      waitForCart(timeout = 4000) {
        const isReady = (el) => el && typeof el.renderContents === 'function';
        const cart = this.getCart();
        if (isReady(cart)) return Promise.resolve(cart);

        return new Promise((resolve) => {
          const start = Date.now();
          const tick = () => {
            const next = this.getCart();
            if (isReady(next) || Date.now() - start >= timeout) {
              resolve(next);
              return;
            }
            requestAnimationFrame(tick);
          };
          tick();
        });
      }

      flushQueuedSubmit() {
        const queue = window.__atcQueue;
        if (!Array.isArray(queue) || !this.form) return;
        window.__atcQueue = queue.filter((form) => {
          if (form !== this.form) return true;
          this.onSubmitHandler(new Event('submit', { cancelable: true, bubbles: true }));
          return false;
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.appendUpsellItems(formData);

        this.waitForCart()
          .then((cart) => {
            if (cart?.getSectionsToRender) {
              formData.append(
                'sections',
                cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              cart.setActiveElement?.(document.activeElement);
            }
            config.body = formData;
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => response.text())
          .then((text) => {
            let parsed;
            try {
              parsed = JSON.parse(text);
            } catch (error) {
              throw new Error(text || 'Invalid add to cart response');
            }
            return parsed;
          })
          .then((response) => this.addPendingFilterGifts(response))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id') || formData.get('items[0][id]'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const readyCart = this.getCart();
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id') || formData.get('items[0][id]'),
                cartData: response,
              });
            }
            this.error = false;

            if (!readyCart || typeof readyCart.renderContents !== 'function') return;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    readyCart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              readyCart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            const cart = this.getCart();
            if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      appendUpsellItems(formData) {
        const filterSubPayload = this.getFilterSubscriptionPayload();
        if (filterSubPayload?.selection && filterSubPayload.selection !== 'one_time') {
          this.appendFilterSubscriptionItems(formData, filterSubPayload);
          return;
        }

        const extras = this.collectExtraItems();
        if (!extras.length) return;

        const mainVariantId = formData.get('id');
        const quantity = formData.get('quantity') || '1';
        if (!mainVariantId) return;

        formData.delete('id');
        formData.delete('quantity');
        formData.append('items[0][id]', mainVariantId);
        formData.append('items[0][quantity]', quantity);

        extras.forEach((item, index) => {
          const itemIndex = index + 1;
          formData.append(`items[${itemIndex}][id]`, item.id);
          formData.append(`items[${itemIndex}][quantity]`, item.quantity);
          if (item.selling_plan) {
            formData.append(`items[${itemIndex}][selling_plan]`, item.selling_plan);
          }
          if (item.properties) {
            Object.entries(item.properties).forEach(([key, value]) => {
              formData.append(`items[${itemIndex}][properties][${key}]`, value);
            });
          }
        });
      }

      getFilterSubscriptionPayload() {
        const productInfo = this.closest('product-info') || document;
        const block = productInfo.querySelector('filter-subscription-block');
        if (!block || typeof block.getCartPayload !== 'function') return null;
        return block.getCartPayload();
      }

      appendFilterSubscriptionItems(formData, payload) {
        const bundleVariantId = payload.bundle_id;
        if (!bundleVariantId) return;

        const bundleId = `vb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const planKey = payload.selection;

        formData.delete('id');
        formData.delete('quantity');
        formData.delete('selling_plan');

        formData.append('items[0][id]', bundleVariantId);
        formData.append('items[0][quantity]', payload.bundle_qty || '1');
        if (payload.selling_plan) {
          formData.append('items[0][selling_plan]', payload.selling_plan);
        }
        formData.append('items[0][properties][_vb_bundle]', bundleId);
        formData.append('items[0][properties][_vb_role]', 'main');
        formData.append('items[0][properties][_vb_plan]', planKey);

        const gifts = (payload.gifts || []).filter((gift) => gift?.id);
        this._pendingFilterGifts = gifts.length
          ? { gifts, bundleId, planKey }
          : null;
      }

      addPendingFilterGifts(response) {
        const pending = this._pendingFilterGifts;
        this._pendingFilterGifts = null;
        if (response?.status || !pending?.gifts?.length) return Promise.resolve(response);

        return this.addFilterGiftItems(pending.gifts, pending.bundleId, pending.planKey).then((giftResponse) => {
          if (giftResponse && !giftResponse.status) return giftResponse;
          return response;
        });
      }

      addFilterGiftItems(gifts, bundleId, planKey) {
        const addGifts = (giftItems) => {
          if (!giftItems.length) return Promise.resolve({ status: 422 });

          const formData = new FormData();
          giftItems.forEach((gift, index) => {
            formData.append(`items[${index}][id]`, gift.id);
            formData.append(`items[${index}][quantity]`, gift.quantity || '1');
            formData.append(`items[${index}][properties][_vb_bundle]`, bundleId);
            formData.append(`items[${index}][properties][_vb_role]`, 'gift');
            formData.append(`items[${index}][properties][_vb_plan]`, planKey);
            formData.append(`items[${index}][properties][_gift]`, '1');
          });

          const cart = this.getCart();
          if (cart?.getSectionsToRender) {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        };

        return addGifts(gifts).then((response) => {
          if (!response?.status) return response;

          return gifts.reduce(
            (chain, gift) =>
              chain.then((lastResponse) =>
                addGifts([gift]).then((giftResponse) =>
                  giftResponse?.status ? lastResponse : giftResponse
                )
              ),
            Promise.resolve(response)
          );
        });
      }

      collectExtraItems() {
        const extras = [];
        const productInfo = this.closest('product-info') || document;

        const upsellBlock = productInfo.querySelector('product-upsell-block');
        const upsellToggle = upsellBlock?.querySelector('[data-upsell-toggle]');
        const upsellVariantId = upsellBlock?.dataset?.variantId;
        if (upsellToggle?.checked && upsellVariantId) {
          extras.push({ id: upsellVariantId, quantity: '1' });
        }

        const selectedFilter = productInfo.querySelector('product-offer-block [data-filter].is-selected');
        const filterVariantId = selectedFilter?.dataset.variantId;
        if (filterVariantId) {
          const item = { id: filterVariantId, quantity: '1' };
          if (selectedFilter.dataset.filter === 'abonnement' && selectedFilter.dataset.sellingPlan) {
            item.selling_plan = selectedFilter.dataset.sellingPlan;
          }
          extras.push(item);
        }

        const offerBlock = productInfo.querySelector('product-offer-block');
        const secondToggle = offerBlock?.querySelector('[data-second-toggle]');
        if (secondToggle?.checked) {
          const secondCard = offerBlock.querySelector('[data-second-model].is-selected');
          const secondVariantId = secondCard?.dataset.variantId;
          if (secondVariantId) {
            extras.push({ id: secondVariantId, quantity: '1' });
          }
        }

        offerBlock?.querySelectorAll('[data-gift].is-unlocked[data-variant-id]').forEach((gift) => {
          extras.push({
            id: gift.dataset.variantId,
            quantity: gift.dataset.quantity || '1',
            properties: { _gift: '1' },
          });
        });

        return extras;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
